import { useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from 'react-query';
import Subtitle from 'src/components/Subtitle';
import { getFiagroLiabilitySubclassesData } from 'src/services/operation/operationFiagro';
import { FiagroLiabilitySubclassesData } from 'src/types/operations/fiagro';
import { convertNumber, toCurrency } from 'src/utils/number';

import {
  formatReferenceDate,
  getMostRecentLiabilityQuotesBySubclass,
  toPercentage,
} from '../utils';

interface LiabilityQuotesProps {
  hash: string;
}

function LiabilityQuoteRow({ row }: { row: FiagroLiabilitySubclassesData }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        onClick={() => setOpen((prev) => !prev)}
        sx={{ cursor: 'pointer' }}>
        <TableCell width={36}>
          <IconButton aria-label="expandir linha" size="small" tabIndex={-1}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontSize: '0.8em' }}>{row.nomeSubclasse}</TableCell>
        <TableCell align="right" sx={{ fontSize: '0.8em' }}>
          {row.valorPatrimonialCota != null
            ? toCurrency(row.valorPatrimonialCota)
            : 'N/D'}
        </TableCell>
        <TableCell align="right" sx={{ fontSize: '0.8em' }}>
          {row.patrimonioSubclasseEstimado != null
            ? toCurrency(row.patrimonioSubclasseEstimado)
            : 'N/D'}
        </TableCell>
        <TableCell align="right" sx={{ fontSize: '0.8em' }}>
          {formatReferenceDate(row.dataReferencia)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Paper square sx={{ p: 2, mb: 2, backgroundColor: '#f7f7f7' }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                {row.nomeSubclasse}
              </Typography>

              <Grid container>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{ pr: { md: 3 }, mb: { xs: 2, md: 0 } }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    COTAS
                  </Typography>
                  <Box mt={1}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}>
                      Número de cotas
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.numeroCotas != null
                        ? convertNumber(row.numeroCotas)
                        : 'N/D'}
                    </Typography>
                  </Box>
                  <Box mt={1}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}>
                      Valor Patrimonial
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.valorPatrimonialCota != null
                        ? toCurrency(row.valorPatrimonialCota)
                        : 'N/D'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    pl: { md: 3 },
                    borderLeft: { md: '1px dashed #ccc' },
                  }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    PARTICIPAÇÕES
                  </Typography>
                  <Box mt={1}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}>
                      Participação no PL do fundo
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {toPercentage(row.pesoPatrimonioSubclasse)}
                    </Typography>
                  </Box>
                  <Box mt={1}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}>
                      Participação no núm. de cotas do fundo
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {toPercentage(row.pesoNumeroCotasSubclasse)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function LiabilityQuotes({ hash }: LiabilityQuotesProps) {
  const {
    data = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['operationFIAGROLiabilitySubclasses', hash],
    queryFn: () => getFiagroLiabilitySubclassesData(hash),
    enabled: !!hash,
  });

  if (isLoading) {
    return (
      <Box>
        <Skeleton
          variant="text"
          width={120}
          height={20}
          sx={{ mb: 1, mt: 3 }}
        />
        <Box sx={{ height: 2, backgroundColor: 'black', mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={200} />
      </Box>
    );
  }

  if (isError) {
    return (
      <>
        <Subtitle title="Cotas" />
        <Alert severity="error">Erro ao carregar os dados de cotas.</Alert>
      </>
    );
  }

  if (!data.length) {
    return (
      <>
        <Subtitle title="Cotas" />
        <Alert severity="info">Nenhum dado de cotas disponível.</Alert>
      </>
    );
  }

  return (
    <>
      <Subtitle title="Cotas" />
      <TableContainer component={Paper} square sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={36} />
              <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>
                Nome da subclasse
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontSize: '12px', fontWeight: 600 }}>
                Valor
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontSize: '12px', fontWeight: 600 }}>
                Patrimônio Líquido
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontSize: '12px', fontWeight: 600 }}>
                Data de Referência
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getMostRecentLiabilityQuotesBySubclass(data).map((row) => (
              <LiabilityQuoteRow
                key={`${row.id}-${row.dataReferencia}`}
                row={row}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
